// Piezas de interfaz compartidas por el constructor de presupuestos
// (budget-builder.jsx) y la página Tarifario (tariff.jsx).
import React from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons.jsx';
import { TAG_TONES } from '../data/budget-catalog.js';

const { useState, useEffect, useMemo, useRef, useCallback } = React;

// ---------- Formato ----------
const EUR = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' });
const EUR0 = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, useGrouping: 'always' });
const NUM = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2, useGrouping: 'always' });
export const fmtEur = (n) => (n == null || !Number.isFinite(n) ? '—' : EUR.format(n));
export const fmtEur0 = (n) => (n == null || !Number.isFinite(n) ? '—' : EUR0.format(n));
export const fmtNum = (n) => (n == null || !Number.isFinite(Number(n)) ? '—' : NUM.format(Number(n)));
export const fmtPct = (n, d = 1) => (n == null || !Number.isFinite(n) ? '—' : `${n.toFixed(d).replace('.', ',')} %`);
export const round2 = (n) => Math.round(n * 100) / 100;
export const normalize = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
export const UNIT_SHORT = { hora: 'h', persona: 'pax', trayecto: 'tray.', ud: 'ud', jornada: 'jorn.', mes: 'mes' };

// Acepta "1.760", "1760", "42,51", "1.234,5" o "42.51".
export function parseEs(raw) {
  let v = String(raw ?? '').trim().replace(/\s|€|%/g, '');
  if (!v) return null;
  if (v.includes(',')) v = v.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(v)) v = v.replace(/\./g, '');
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 45) return 'hace unos segundos';
  if (s < 3600) return `hace ${Math.round(s / 60)} min`;
  if (s < 86400) return `hace ${Math.round(s / 3600)} h`;
  if (s < 86400 * 7) return `hace ${Math.round(s / 86400)} d`;
  return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

export const tagTone = (tag, tagByName) => (tag && tagByName?.[tag]?.tone) || (tag ? 'slate' : 'none');

// ---------- Etiqueta (píldora con punto de color + nombre) ----------
export function TagPill({ tag, tone, size = 'md', muted, emptyLabel = 'Sin etiqueta' }) {
  if (!tag) return <span className={`tg-pill tg-none${size === 'sm' ? ' is-sm' : ''}`}>{emptyLabel}</span>;
  return (
    <span className={`tg-pill tg-${tone || 'slate'}${size === 'sm' ? ' is-sm' : ''}${muted ? ' is-muted' : ''}`}>
      <span className="tg-dot"/>{tag}
    </span>
  );
}

// ---------- Popover posicionado (fixed) que no se recorta con el scroll de la tabla ----------
export function usePopover(width = 380, height = 380) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);

  const place = useCallback(() => {
    const r = anchorRef.current?.getBoundingClientRect();
    if (!r) return;
    const w = Math.min(width, window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    const below = window.innerHeight - r.bottom;
    const top = below > height + 8 ? r.bottom + 4 : Math.max(8, r.top - height - 4);
    setPos({ left, top, width: w });
  }, [width, height]);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const close = (e) => {
      if (anchorRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // Al desplazar, el panel acompaña a su celda; se cierra si la celda sale de la vista.
    const onScroll = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) setOpen(false);
      else place();
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  return { open, setOpen, pos, anchorRef, panelRef };
}

// Panel del popover, montado en <body>: así no lo recorta ni lo desplaza el
// contenedor con scroll de la tabla (ni un ancestro con transform).
export function PopPanel({ pop, children }) {
  if (!pop.open || !pop.pos) return null;
  return createPortal(
    <div ref={pop.panelRef} className="pp" style={{ left: pop.pos.left, top: pop.pos.top, width: pop.pos.width }}>{children}</div>,
    document.body,
  );
}

// Lista con buscador y teclado (↑ ↓ Enter Esc) para los selectores.
function SearchList({ options, value, onPick, onClose, placeholder, renderOption, emptyText, maxHeight = 330 }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const nq = normalize(q);
  // Al buscar, la opción vacía («Sin etiqueta», «Sin tarifa») desaparece para que Enter elija la primera coincidencia.
  const filtered = useMemo(() => (nq ? options.filter((o) => !o.always && normalize(o.search).includes(nq)) : options), [options, nq]);
  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => { listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [active]);
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(filtered.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[active]) onPick(filtered[active].value); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };
  let lastGroup = null;
  return (
    <div onKeyDown={onKey}>
      <div className="pp-search">
        <Icon.Search size={14}/>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder}/>
      </div>
      <div ref={listRef} className="pp-list" style={{ maxHeight }} role="listbox">
        {!filtered.length && q && <div className="pp-empty">{emptyText || `Nada coincide con «${q}».`}</div>}
        {filtered.map((o, i) => {
          const header = o.group && o.group !== lastGroup ? o.group : null;
          if (o.group) lastGroup = o.group;
          return (
            <React.Fragment key={o.value || '__none'}>
              {header && <div className="pp-group">{header}</div>}
              <div data-idx={i} role="option" aria-selected={o.value === value}
                className={`pp-item${i === active ? ' is-active' : ''}${o.value === value ? ' is-selected' : ''}`}
                onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); onPick(o.value); }}>
                {renderOption(o)}
                {o.value === value && <Icon.Check size={13}/>}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Selector de etiqueta ----------
export function TagPicker({ value, tags, tagByName, onChange, disabled, compact, emptyLabel = 'Sin etiqueta' }) {
  const pop = usePopover(300, 360);
  const options = useMemo(() => [
    { value: '', search: '', always: true, label: emptyLabel },
    ...tags.map((t) => ({ value: t.name, search: `${t.name} ${t.desc || ''}`, label: t.name, tone: t.tone, desc: t.desc })),
  ], [tags, emptyLabel]);
  const pick = (v) => { onChange(v); pop.setOpen(false); };
  return (
    <>
      <button ref={pop.anchorRef} type="button" className={`cell-btn${compact ? ' is-compact' : ''}`} disabled={disabled}
        onClick={() => pop.setOpen((o) => !o)} title={tagByName?.[value]?.desc || 'Etiqueta de gasto'}>
        <TagPill tag={value} tone={tagTone(value, tagByName)} emptyLabel={emptyLabel}/>
        {!disabled && <Icon.ChevronDown size={12} className="cell-btn-caret"/>}
      </button>
      <PopPanel pop={pop}>
          <SearchList options={options} value={value || ''} onPick={pick} onClose={() => pop.setOpen(false)} placeholder="Buscar etiqueta"
            renderOption={(o) => (o.value
              ? <span className="pp-main"><TagPill tag={o.label} tone={o.tone}/></span>
              : <span className="pp-main" style={{ color: 'var(--text-3)' }}>{o.label}</span>)}/>
      </PopPanel>
    </>
  );
}

// ---------- Selector del tarifario ----------
export const cleanTariffName = (t) => String(t?.name || '');

export function TariffPicker({ value, catalog, onChange, disabled, snapName }) {
  const pop = usePopover(460, 400);
  const t = value ? catalog.byId[value] : null;
  const options = useMemo(() => [
    { value: '', search: '', always: true, label: 'Sin tarifa · estimar coste' },
    ...catalog.active.map((x) => ({
      value: x.id, group: x.group, search: `${x.id} ${x.name} ${x.group} ${x.tag}`, item: x,
    })),
  ], [catalog]);
  const pick = (v) => { onChange(v); pop.setOpen(false); pop.anchorRef.current?.focus(); };
  const name = t ? t.name : (value ? snapName || value : '');
  return (
    <>
      <button ref={pop.anchorRef} type="button" className="cell-btn tp-btn" disabled={disabled} onClick={() => pop.setOpen((o) => !o)}
        title={t ? `${t.id} · ${t.name}${t.desc ? `\n${t.desc}` : ''}` : value ? `${value} (ya no está en el tarifario)` : 'Sin partida del tarifario: se usa el coste estimado'}>
        {value ? (
          <>
            <span className="code-badge">{value}</span>
            <span className="tp-name">{name}</span>
          </>
        ) : <span className="tp-name is-empty">Sin tarifa · estimar</span>}
        {!disabled && <Icon.ChevronDown size={12} className="cell-btn-caret"/>}
      </button>
      <PopPanel pop={pop}>
          <SearchList options={options} value={value || ''} onPick={pick} onClose={() => pop.setOpen(false)}
            placeholder="Buscar en el tarifario (código, perfil, concepto…)" maxHeight={340}
            renderOption={(o) => (o.item ? (
              <>
                <span className="pp-main">
                  <span className="code-badge">{o.item.id}</span>
                  <span className="pp-text">{o.item.name}</span>
                </span>
                <span className="pp-meta">
                  {o.item.rate != null && o.item.cost != null
                    ? <>{fmtEur(o.item.rate)}<span className="pp-meta-sub">/{UNIT_SHORT[o.item.unit] || o.item.unit} · coste {fmtEur(o.item.cost)}</span></>
                    : <span className="pp-meta-sub">sin importe</span>}
                </span>
              </>
            ) : <span className="pp-main" style={{ color: 'var(--text-3)' }}>{o.label}</span>)}/>
          <div className="pp-foot">
            <Icon.Info size={12}/> Los importes salen de la página <a href="/tariff" target="_blank" rel="noreferrer">Tarifario</a>.
          </div>
      </PopPanel>
    </>
  );
}

// ---------- Campo numérico tipo hoja de cálculo ----------
// Muestra "1.760" o "42,51", acepta coma o punto. Confirma al salir o con
// Enter; Escape descarta. `ghost` = sin borde hasta hover/foco.
export function NumField({ value, onCommit, placeholder = '—', disabled, ariaLabel, suffix, allowEmpty = true, ghost = true, width, align = 'right', invalid, decimals = 2 }) {
  const [draft, setDraft] = useState(null);
  const fmt = (n) => (n == null || n === '' || !Number.isFinite(Number(n)) ? '' : new Intl.NumberFormat('es-ES', { maximumFractionDigits: decimals, useGrouping: 'always' }).format(Number(n)));
  const shown = draft != null ? draft : fmt(value);
  return (
    <div className={`nf${ghost ? ' is-ghost' : ''}${invalid ? ' is-invalid' : ''}${disabled ? ' is-disabled' : ''}`} style={width ? { width } : undefined}>
      <input type="text" inputMode="decimal" value={shown} placeholder={placeholder} disabled={disabled} aria-label={ariaLabel}
        style={{ textAlign: align, paddingRight: suffix ? 30 : undefined }}
        onFocus={(e) => { setDraft(value == null || value === '' ? '' : String(round2(Number(value))).replace('.', ',')); requestAnimationFrame(() => e.target.select()); }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setDraft(null); requestAnimationFrame(() => e.target.blur()); } }}
        onBlur={() => {
          if (draft == null) return;
          const d = draft;
          setDraft(null);
          if (!d.trim()) { if (allowEmpty && value != null && value !== '') onCommit(null); return; }
          const v = parseEs(d);
          if (v != null && v >= 0 && (value == null || value === '' || Math.abs(v - Number(value)) > 0.004)) onCommit(v);
        }}/>
      {suffix && <span className="nf-suffix">{suffix}</span>}
    </div>
  );
}

// Campo de texto con el mismo aspecto (confirma al salir).
export function TextField({ value, onCommit, placeholder, disabled, ariaLabel, strong, multiline, rows = 2, submitOnEnter }) {
  const [draft, setDraft] = useState(null);
  const shown = draft != null ? draft : (value || '');
  const common = {
    value: shown, placeholder, disabled, 'aria-label': ariaLabel,
    onFocus: () => setDraft(value || ''),
    onChange: (e) => setDraft(e.target.value),
    onKeyDown: (e) => {
      if (e.key === 'Enter' && (!multiline || submitOnEnter || e.metaKey || e.ctrlKey)) { e.preventDefault(); e.currentTarget.blur(); }
      if (e.key === 'Escape') { setDraft(null); const el = e.currentTarget; requestAnimationFrame(() => el.blur()); }
    },
    onBlur: () => { if (draft == null) return; const d = draft.trim(); setDraft(null); if (d !== (value || '').trim()) onCommit(d); },
  };
  return (
    <div className={`nf is-ghost is-text${strong ? ' is-strong' : ''}${disabled ? ' is-disabled' : ''}`}>
      {multiline ? <textarea rows={rows} {...common}/> : <input type="text" {...common}/>}
    </div>
  );
}

// Selector de color de etiqueta.
export function ToneSwatches({ value, onChange }) {
  return (
    <div className="tone-swatches" role="radiogroup" aria-label="Color">
      {TAG_TONES.map((t) => (
        <button key={t} type="button" role="radio" aria-checked={value === t} className={`tone-swatch tg-${t}${value === t ? ' is-active' : ''}`}
          title={t} onClick={() => onChange(t)}><span className="tg-dot"/></button>
      ))}
    </div>
  );
}
